import React, { useState, useRef, useCallback } from "react";
import { toast } from "@/lib/toast";
import { Bug, Send, RefreshCw, Smartphone, CheckCircle2, RotateCcw } from "lucide-react";
import TopBar from "../components/TopBar";
import { useDistrict } from "../lib/district-context";
import { useOnlineSync } from "../hooks/useOnlineSync";
import { submitBugReport, uploadScreenshot } from "../lib/bug-reports";

const ROUTE_LABELS = {
  "/":          "Inicio",
  "/churches":  "Iglesias",
  "/visits":    "Visitas",
  "/prayers":   "Oraciones",
  "/district":  "Distrito",
  "/notes":     "Notas",
  "/agenda":    "Agenda",
  "/families":  "Familias",
  "/people":    "Personas",
  "/preaching": "Predicación",
  "/materials": "Materiales",
  "/baptisms":  "Bautismos",
  "/profile":   "Perfil",
};

function getRouteLabel(pathname) {
  if (!pathname) return "Kéves";
  const match = Object.keys(ROUTE_LABELS).find((k) =>
    k === "/" ? pathname === "/" : pathname === k || pathname.startsWith(k + "/")
  );
  return match ? ROUTE_LABELS[match] : "Kéves";
}

export function SettingsSupportContent({ backgroundRoute, backgroundScreenshot }) {
  const { district, userProfile } = useDistrict();
  const { pendingCount, outboxStatus } = useOnlineSync();

  const [description, setDescription] = useState("");
  const [sending,     setSending]     = useState(false);
  const [sent,        setSent]        = useState(false);
  const textareaRef = useRef(null);

  const routeLabel = getRouteLabel(backgroundRoute);

  // Auto-grow textarea: expands naturally up to ~220px, then scrolls.
  const handleDescriptionChange = useCallback((e) => {
    setDescription(e.target.value);
    const el = textareaRef.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = Math.min(el.scrollHeight, 220) + "px";
    }
  }, []);

  const handleSubmit = async () => {
    if (!description.trim()) {
      toast.error("Describe brevemente qué ocurrió antes de enviar.");
      return;
    }

    setSending(true);
    try {
      // Screenshot upload is best-effort — failure doesn't block the report
      let screenshot_url = null;
      if (backgroundScreenshot) {
        screenshot_url = await uploadScreenshot(backgroundScreenshot);
      }

      await submitBugReport({
        description:   description.trim(),
        route:         backgroundRoute || window.location.pathname,
        screenshot_url,
        device_info: {
          userAgent:   navigator.userAgent,
          online:      navigator.onLine,
          pendingSync: pendingCount,
          failedSync:  outboxStatus?.failed || 0,
        },
        app_version:  process.env.REACT_APP_VERSION || null,
        user_id:      userProfile?.id  || null,
        district_id:  district?.id     || null,
      });

      setSent(true);
      setDescription("");
      if (textareaRef.current) textareaRef.current.style.height = "auto";
    } catch {
      toast.error("No se pudo enviar el reporte. Inténtalo más tarde.");
    } finally {
      setSending(false);
    }
  };

  // ── Confirmation ───────────────────────────────────────────────────────────
  if (sent) {
    return (
      <div className="px-4 py-10 flex flex-col items-center text-center space-y-4">
        <div className="h-14 w-14 rounded-full bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center">
          <CheckCircle2 size={26} className="text-emerald-500" />
        </div>
        <div className="space-y-1.5">
          <p className="text-[16px] font-semibold text-slate-900 dark:text-slate-100">
            Reporte enviado
          </p>
          <p className="text-[13px] text-slate-500 dark:text-slate-400 leading-snug max-w-[220px]">
            Gracias. Lo revisaré y trabajaré en una solución.
          </p>
        </div>
        <button
          onClick={() => setSent(false)}
          className="flex items-center gap-1.5 text-[12px] text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors duration-150 mt-1"
        >
          <RotateCcw size={11} />
          Enviar otro reporte
        </button>
      </div>
    );
  }

  // ── Report form ────────────────────────────────────────────────────────────
  return (
    <div className="px-4 py-5 space-y-5">

      {/* Header */}
      <div className="flex items-start gap-3.5">
        <div className="h-11 w-11 rounded-[14px] bg-rose-50 dark:bg-rose-900/20 flex items-center justify-center shrink-0 mt-0.5">
          <Bug size={19} className="text-rose-500 dark:text-rose-400" />
        </div>
        <div className="flex-1 min-w-0 pt-0.5">
          <h2 className="text-[17px] font-semibold text-slate-900 dark:text-slate-100 tracking-tight">
            ¿Qué ocurrió?
          </h2>
          <p className="text-[13px] text-slate-500 dark:text-slate-400 mt-0.5 leading-snug">
            Describe lo que viste o lo que esperabas que ocurriera.
          </p>
        </div>
      </div>

      {/* Auto-grow textarea */}
      <textarea
        ref={textareaRef}
        value={description}
        onChange={handleDescriptionChange}
        placeholder="Por ejemplo: intenté guardar una visita y la pantalla se quedó cargando…"
        style={{ minHeight: "120px", maxHeight: "220px" }}
        className="
          w-full rounded-[16px]
          bg-white dark:bg-slate-800
          border border-slate-200 dark:border-slate-700
          px-4 py-3.5
          text-[14px] text-slate-900 dark:text-slate-100
          placeholder:text-slate-400 dark:placeholder:text-slate-500
          focus:outline-none
          focus:ring-2 focus:ring-brand/10 dark:focus:ring-blue-900/40
          focus:border-brand/50 dark:focus:border-blue-500
          resize-none overflow-y-auto
          transition-all duration-200 leading-relaxed
        "
      />

      {/* Context card — "Pantalla incluida" */}
      <div className="rounded-[16px] bg-slate-50 dark:bg-slate-800/60 border border-slate-100 dark:border-slate-700/50 overflow-hidden">
        <div className="flex items-center gap-3 p-3.5">

          {/* Phone-ratio thumbnail */}
          {backgroundScreenshot ? (
            <div
              className="rounded-[10px] overflow-hidden shrink-0 border border-slate-200/60 dark:border-slate-600/40 shadow-sm bg-slate-100 dark:bg-slate-700"
              style={{ width: 44, height: 72 }}
            >
              <img
                src={backgroundScreenshot}
                alt=""
                aria-hidden="true"
                className="w-full h-full object-cover object-top"
              />
            </div>
          ) : (
            <div
              className="rounded-[10px] shrink-0 border border-slate-100 dark:border-slate-700/50 bg-slate-100 dark:bg-slate-700/50 flex items-center justify-center"
              style={{ width: 44, height: 72 }}
            >
              <Smartphone size={14} className="text-slate-300 dark:text-slate-500" />
            </div>
          )}

          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-semibold text-slate-700 dark:text-slate-300">
              Pantalla incluida
            </p>
            <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5 truncate">
              {routeLabel}
            </p>
          </div>

          <div className="h-5 w-5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center shrink-0">
            <CheckCircle2 size={10} className="text-emerald-600 dark:text-emerald-400" />
          </div>

        </div>
      </div>

      {/* Submit CTA */}
      <div className="space-y-2.5">
        <button
          onClick={handleSubmit}
          disabled={sending || !description.trim()}
          className="
            w-full h-11 rounded-[14px]
            bg-brand text-white
            text-[13px] font-semibold
            flex items-center justify-center gap-2
            transition-all duration-200
            active:scale-[0.98] active:opacity-80
            shadow-sm disabled:opacity-40
          "
        >
          {sending
            ? <RefreshCw size={14} className="animate-spin" />
            : <Send size={15} />
          }
          {sending ? "Enviando…" : "Enviar reporte"}
        </button>
        <p className="text-[11px] text-slate-400 dark:text-slate-500 text-center">
          Tu reporte ayudará a mejorar Kéves.
        </p>
      </div>

    </div>
  );
}

export default function SettingsSupport() {
  return (
    <div className="anim-in min-h-[100dvh] bg-page-bg pb-[calc(96px+env(safe-area-inset-bottom))] font-sans">
      <TopBar title="Ayuda" safeFallback="/settings" />
      <SettingsSupportContent />
    </div>
  );
}
